/**
 * Componente FormField
 * 
 * Campo de formulário reutilizável com validação e feedback visual
 */

import React, { useState, useEffect } from 'react';

const FormField = ({
  label,
  name,
  type = 'text',
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  required = false,
  disabled = false,
  autoComplete,
  className = '',
  validationRules = [],
  showErrorOnBlur = true,
  ...props
}) => {
  const [touched, setTouched] = useState(false);
  const [localError, setLocalError] = useState('');

  // Validação em tempo real
  useEffect(() => {
    if (touched && validationRules.length > 0) {
      for (const rule of validationRules) {
        const errorMessage = rule(value);
        if (errorMessage) {
          setLocalError(errorMessage);
          return;
        }
      }
      setLocalError('');
    }
  }, [value, touched, validationRules]);

  const handleBlur = (e) => {
    setTouched(true);
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleChange = (e) => {
    if (onChange) {
      onChange(e);
    }
    // Limpar erro quando começar a digitar
    if (localError) {
      setLocalError('');
    }
  };

  const displayError = error || (showErrorOnBlur && touched && localError);
  const hasError = !!displayError;

  return (
    <div className={`form-field ${className}`.trim()}>
      {label && (
        <label 
          htmlFor={name} 
          className="form-field-label"
          style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: '500',
            fontSize: '14px',
            color: '#374151',
          }}
        >
          {label}
          {required && <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>}
        </label>
      )}

      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoComplete={autoComplete}
        aria-invalid={hasError}
        aria-describedby={hasError ? `${name}-error` : undefined}
        style={{
          width: '100%',
          padding: '12px',
          border: hasError ? '2px solid #ef4444' : '1px solid #d1d5db',
          borderRadius: '6px',
          fontSize: '14px',
          transition: 'all 0.2s ease',
          backgroundColor: disabled ? '#f3f4f6' : '#fff',
          color: disabled ? '#6b7280' : '#111827',
          ...(hasError && {
            boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.1)',
          }),
        }}
        {...props}
      />

      {hasError && (
        <p
          id={`${name}-error`}
          role="alert"
          className="form-field-error"
          style={{
            color: '#ef4444',
            fontSize: '12px',
            marginTop: '6px',
            marginBottom: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <span aria-hidden="true">⚠️</span>
          {displayError}
        </p>
      )}
    </div>
  );
};

export default FormField;

